/**
 * The line the page ends on (contracts/web-surface.md §2). Two sentences and
 * two links under a hairline rule, and nothing else.
 *
 * The point-of-collection notice inside the form remains the disclosure a
 * visitor reads where they type (FR-016); the privacy policy page exists
 * because App Store Connect requires a hosted policy URL for the iOS app, the
 * support page because it requires a support URL a reviewer can follow, and
 * this footer is where a person expects to find both.
 *
 * ## Why the tracking sentence is here (FR-017, SC-010)
 *
 * The page loads no analytics, advertising, or behavioural-tracking script, and
 * this footer is the last place that could quietly have added one. Saying so in
 * the visitor's own words costs a line and makes the guarantee legible to the
 * person it protects, who is otherwise asked to take it on faith while typing
 * an address in. The claim is narrow on purpose — it is about *scripts*, which
 * is exactly what FR-017 promises and what T053's request inventory checks.
 *
 * ## Layout
 *
 * The rule is drawn on the inner column rather than on the outer view, so it
 * starts and ends where the text above it does instead of running out to the
 * window edge. The horizontal padding is the same `spacing.md` the hero and the
 * cards each own: the T049 container adds only the centred 1100 px column, so
 * this inset is the page's whole gutter at every width, down to the 320 px
 * reflow floor FR-019(f) sets.
 *
 * `FeatureCards` already leaves `spacing.xxl` below the last card, which is the
 * gap above the rule; the padding here only has to hold the rule off the text
 * and give the page a closing breath.
 *
 * `role="contentinfo"` is what makes react-native-web emit a real `<footer>` —
 * the landmark a screen reader jumps to — rather than one more `<div>`.
 */

import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";

const NO_TRACKING_NOTICE =
  "This page loads no analytics, advertising, or tracking scripts.";

/**
 * Built once at import rather than on every render. Nobody holds this page open
 * across a new year, and deriving it here keeps the year from going stale in a
 * string literal a year from now.
 */
const COLOPHON = `© ${new Date().getFullYear()} Sparkie`;

/**
 * A real `<a>` (the site-header brand-link precedent): focusable, openable in
 * a new tab, announced as a link. Padding keeps the focus ring off the text.
 */
const POLICY_LINK_STYLE: CSSProperties = {
  borderRadius: radius.sm,
  padding: spacing.xxs,
};

export function Footer() {
  const theme = useAppTheme();

  const quietText = [styles.centered, { color: theme.colors.onSurfaceVariant }];

  return (
    <View role="contentinfo" style={styles.footer}>
      <View
        style={[styles.column, { borderTopColor: theme.colors.outlineVariant }]}
      >
        <Text variant="bodySmall" style={quietText}>
          {NO_TRACKING_NOTICE}
        </Text>

        <View style={styles.links}>
          <Link
            to="/privacy"
            style={{
              ...POLICY_LINK_STYLE,
              color: theme.colors.onSurfaceVariant,
            }}
          >
            <Text
              variant="bodySmall"
              style={[
                styles.centered,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Privacy policy
            </Text>
          </Link>
          <Link
            to="/support"
            style={{
              ...POLICY_LINK_STYLE,
              color: theme.colors.onSurfaceVariant,
            }}
          >
            <Text
              variant="bodySmall"
              style={[
                styles.centered,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Support
            </Text>
          </Link>
        </View>

        <Text variant="bodySmall" style={quietText}>
          {COLOPHON}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    paddingHorizontal: spacing.md,
  },
  column: {
    alignItems: "center",
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: spacing.xxs,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  links: {
    flexDirection: "row",
    gap: spacing.md,
  },
  // Paper's `Text` sets `textAlign: 'left'` on every variant, so a line that
  // wraps at a narrow width only stays centred if this says so.
  centered: {
    textAlign: "center",
  },
});
